import { AlertCircle } from "lucide-react";
import type * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Soft-tone empty state. Use for "no results yet" / "empty list" rather than
 * for error conditions — those should use {@link ErrorBanner}.
 */
export function EmptyState({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-dashed border-border bg-card/50 px-6 py-12 text-center text-sm text-muted-foreground",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Inline error banner. Pair with `try/catch` around server actions or fetches
 * to surface a non-fatal error to the user without unmounting the page.
 */
export function ErrorBanner({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive",
        className,
      )}
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="break-words">{children}</div>
    </div>
  );
}
